import { useEffect, useId, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Loader2, Mail } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useAuth, userApi, type DigestSettingsResponse } from '@/features/auth'
import { getErrorMessage } from '@/lib/axios'
import { toast } from '@/lib/toast'

interface DigestSettingsProps {
  /** Keywords registered on this device. Compared against the stored list. */
  keywords: string[]
}

/**
 * Morning digest opt-in (P3-1 had no screen).
 *
 * This button used to write the keywords too, which made pressing it the only way the
 * digest ever learned about a keyword change. `useKeywordServerSync` does that
 * automatically now (P3-20) and the button owns the subscription alone.
 *
 * What the sync cannot decide for the user is which device wins: the stored list is
 * whichever browser edited last, so this screen renders it whenever it disagrees with
 * the one on screen, plus a way to take it over. That disagreement is real and the
 * only alternative to showing it is guessing on the user's behalf.
 *
 * Signed out there is nothing to show: the digest needs a stored address and a
 * verified owner, both of which come from the account.
 */
export function DigestSettings({ keywords }: DigestSettingsProps) {
  const { user } = useAuth()
  const userId = user?.preferences.user_id
  // The sidebar renders this twice (mobile <details> + desktop column) and only one
  // is visible per breakpoint, so fixed ids would collide and point every label at
  // the hidden copy.
  const toggleId = useId()
  const emailId = useId()
  const queryClient = useQueryClient()

  const { data: saved } = useQuery({
    queryKey: ['digest-settings', userId],
    queryFn: () => userApi.getDigestSettings(userId as string),
    enabled: Boolean(userId),
  })

  const [enabled, setEnabled] = useState(false)
  const [email, setEmail] = useState('')

  // Server state is the truth for a subscription — a toggle that renders "off" for
  // someone already opted in hides the way back out.
  useEffect(() => {
    if (!saved) return
    setEnabled(saved.enabled)
    setEmail(saved.email ?? user?.firebaseUser.email ?? '')
  }, [saved, user])

  const { mutate: save, isPending } = useMutation({
    mutationFn: () =>
      userApi.updateDigestSettings(userId as string, {
        enabled,
        email: email.trim() || null,
      }),
    onSuccess: (settings) => {
      queryClient.setQueryData(['digest-settings', userId], settings)
      toast.success(
        settings.enabled ? '아침 브리핑을 켰어요' : '아침 브리핑을 껐어요',
        settings.enabled ? `${settings.email}로 보내드려요` : undefined
      )
    },
    onError: (error) => {
      toast.error('저장하지 못했어요', getErrorMessage(error))
    },
  })

  // The list the digest job will read. Sorted before comparing: the digest does not
  // care about order, so a reorder is not a disagreement worth reporting.
  const storedKeywords = saved?.registered_keywords
  const diverged =
    storedKeywords !== undefined &&
    [...storedKeywords].sort().join('\u0000') !== [...keywords].sort().join('\u0000')

  const { mutate: adopt, isPending: isAdopting } = useMutation({
    mutationFn: async () => {
      await userApi.saveRegisteredKeywords(userId as string, keywords)
      return keywords
    },
    onSuccess: (next) => {
      queryClient.setQueryData<DigestSettingsResponse>(['digest-settings', userId], (prev) =>
        prev ? { ...prev, registered_keywords: next } : prev
      )
      toast.success('브리핑 키워드를 이 기기 것으로 맞췄어요')
    },
    onError: (error) => {
      toast.error('맞추지 못했어요', getErrorMessage(error))
    },
  })

  if (!userId) return null

  const missingEmail = enabled && email.trim().length === 0

  return (
    <div className="space-y-3">
      <h2 className="flex items-center gap-2 text-base font-semibold">
        <Mail className="h-4 w-4 text-muted-foreground" />
        아침 브리핑
      </h2>

      <div className="flex items-start gap-3">
        <Checkbox
          id={toggleId}
          checked={enabled}
          onCheckedChange={(checked) => setEnabled(checked as boolean)}
          className="mt-0.5"
        />
        <div className="flex-1 space-y-1">
          <Label htmlFor={toggleId} className="text-sm font-medium leading-none">
            아침 브리핑 받기
          </Label>
          <p className="text-xs leading-relaxed text-muted-foreground">
            매일 아침 6시(한국 시간)에 등록한 키워드의 새 기사 목록을 메일로 보내드려요. 이전에 보낸
            기사는 빼고, 새 기사가 없는 날은 보내지 않아요. 광고나 홍보 내용은 담기지 않고, 언제든
            이 화면에서 끌 수 있어요.
          </p>
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor={emailId} className="text-sm font-normal">
          받을 이메일
        </Label>
        <Input
          id={emailId}
          type="email"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          placeholder="name@example.com"
          autoComplete="email"
        />
      </div>

      <p className="text-xs text-muted-foreground">
        {keywords.length > 0
          ? `이 기기에 등록한 키워드 ${keywords.length}개로 브리핑을 만들어요. 키워드를 바꾸면 따로 저장하지 않아도 반영돼요.`
          : '등록한 키워드가 없어요. 키워드를 먼저 등록해야 브리핑에 담을 기사가 생겨요.'}
      </p>

      {/*
        키워드는 기기마다 따로 저장돼서 마지막으로 바꾼 기기의 목록이 브리핑에 쓰여요.
        말없이 다른 기기 목록을 쓰는 것보다, 어긋났다고 알리고 고를 수 있게 하는 편이 나아요.
      */}
      {storedKeywords && diverged && (
        <div className="space-y-2 rounded-md border border-amber-500/40 bg-amber-500/5 p-3">
          <p className="text-xs leading-relaxed text-muted-foreground">
            {storedKeywords.length > 0
              ? `지금 브리핑은 ${storedKeywords.join(', ')} 키워드를 쓰고 있어요. 다른 기기에서 마지막으로 바꾼 목록이라 이 화면과 달라요.`
              : '브리핑에 저장된 키워드가 아직 없어요. 이 화면의 키워드는 반영되지 않은 상태예요.'}
          </p>
          <Button
            onClick={() => adopt()}
            disabled={isAdopting || keywords.length === 0}
            variant="outline"
            size="sm"
            className="w-full"
          >
            {isAdopting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}이 기기 키워드로
            맞추기
          </Button>
        </div>
      )}

      <Button
        onClick={() => save()}
        disabled={isPending || missingEmail}
        variant="outline"
        size="sm"
        className="w-full"
      >
        {isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
        {isPending ? '저장 중...' : '브리핑 설정 저장'}
      </Button>

      {missingEmail && <p className="text-xs text-destructive">받을 이메일을 입력해 주세요.</p>}

      {saved?.last_sent_at && (
        <p className="text-xs text-muted-foreground">
          마지막 발송 {new Date(saved.last_sent_at).toLocaleDateString('ko-KR')}
        </p>
      )}
    </div>
  )
}
